import React from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import * as PropTypes from 'prop-types';
import { variant } from 'styled-system';

import { useDirection } from '../../hooks';
import { useGetThemeVersion } from '../../hooks/useGetThemeVersion';
import BaseBox from '../../internal/components/BaseBox';
import { linearGradient } from '../../internal/styles/linearGradient';
import sx from '../../internal/styles/sx';
import { StyledBaseCard } from '../Card';
import HighlightedCard from '../HighlightedCard';

const sizeVariants = {
  full: {
    display: 'flex',
    fontSize: 'highlightedHeader.full',
    lineHeight: 'highlightedHeader.full',
    px: 4,
    py: 2,
    borderTopLeftRadius: 'cardRadii.full',
    borderTopRightRadius: 'cardRadii.full',
  },
  small: {
    display: 'inline-flex',
    fontSize: 'bodies.xSmall',
    lineHeight: 'highlightedHeader.small',
    px: 2,
    py: 1,
    borderTopLeftRadius: 'cardRadii.small',
    borderTopRightRadius: 'cardRadii.small',
  },
};

const StyledHighlightedHeader = styled(BaseBox, {
  shouldForwardProp: (prop) =>
    shouldForwardProp(prop) && prop !== 'type' && prop !== 'size',
})(
  css({
    display: 'inline-flex',
    fontFamily: 'body',
    fontWeight: 'medium',
    color: 'white',
  }),
  ({ isCobalt }) =>
    css(
      isCobalt
        ? {}
        : {
            textShadow: '0',
          }
    ),
  variant({
    prop: 'type',
    variants: {
      positive: {
        backgroundColor: 'positive.50',
      },
      informative: {
        backgroundColor: 'info.50',
      },
      warning: {
        backgroundColor: 'warning.50',
      },
      negative: {
        backgroundColor: 'critical.50',
      },
      persuasive: {
        backgroundColor: 'critical.50',
      },
      neutral: {
        backgroundColor: 'neutrals.50',
      },
      promotional: {
        backgroundColor: 'promotional.secondary.default',
      },
      branded: {
        backgroundColor: 'primary.50',
      },
      retail: {
        backgroundImage: linearGradient('180deg', ['deals.30', 'deals.40']),
      },
    },
  }),
  variant({
    prop: 'size',
    variants: sizeVariants,
  }),
  sx
);

const cardZeroBorderTop = {
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
};

const StyledHeaderContainer = styled(BaseBox, {
  shouldForwardProp: (prop) =>
    shouldForwardProp(prop) && prop !== 'type' && prop !== 'size',
})(
  css({
    position: 'relative',
    '::after': {
      content: '""',
      display: 'block',
      left: 0,
      right: 0,
      borderTop: 3,
      borderWidth: 5,
      position: 'absolute',
      zIndex: -1,
    },
  }),
  ({ isCobalt }) =>
    css(
      isCobalt
        ? {
            marginBottom: [-2, 1],
          }
        : {
            [`& + ${HighlightedCard}`]: cardZeroBorderTop,
            [`& + ${StyledBaseCard}`]: cardZeroBorderTop,
          }
    ),
  variant({
    prop: 'type',
    variants: {
      positive: {
        '&::after': { borderColor: 'positive.50' },
      },
      informative: {
        '&::after': { borderColor: 'info.50' },
      },
      warning: {
        '&::after': { borderColor: 'warning.50' },
      },
      negative: {
        '&::after': { borderColor: 'critical.50' },
      },
      persuasive: {
        '&::after': { borderColor: 'critical.50' },
      },
      neutral: {
        '&::after': { borderColor: 'neutrals.50' },
      },
      promotional: {
        '&::after': { borderColor: 'promotional.secondary.default' },
      },
      branded: {
        '&::after': { borderColor: 'primary.50' },
      },
      retail: {
        '&::after': { borderColor: 'deals.40' },
      },
    },
  }),
  variant({
    prop: 'size',
    variants: {
      small: {
        '::after': {
          borderTopRightRadius: 'card',
          borderTopLeftRadius: 'card',
        },
      },
      full: {
        '::after': {},
      },
    },
  }),
  ({ isRtl }) =>
    css({
      '::after': isRtl
        ? { borderTopRightRadius: 0 }
        : { borderTopLeftRadius: 0 },
    }),
  ({ borderSize }) =>
    css(
      borderSize
        ? {
            '::after': {
              borderWidth: borderSize,
            },
          }
        : {}
    )
);

const HighlightedHeader = ({ type, size, children, borderSize, sx }) => {
  const isCobalt = useGetThemeVersion() === 'cobalt';
  return (
    <StyledHeaderContainer
      type={type}
      size={size}
      borderSize={borderSize}
      isCobalt={isCobalt}
      isRtl={useDirection()}
    >
      <StyledHighlightedHeader
        type={type}
        size={size}
        isCobalt={isCobalt}
        sx={sx}
      >
        {children}
      </StyledHighlightedHeader>
    </StyledHeaderContainer>
  );
};

HighlightedHeader.propTypes = {
  type: PropTypes.oneOf([
    'positive',
    'informative',
    'warning',
    'negative',
    'persuasive',
    'neutral',
    'promotional',
    'branded',
    'retail',
  ]).isRequired,
  size: PropTypes.oneOf(Object.keys(sizeVariants)).isRequired,
  borderSize: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default HighlightedHeader;
